import Bugsnag from '@bugsnag/js';
import Link from 'next/link';
import { NextPageContext } from 'next';
import App from 'next/app';
import NextErrorComponent from 'next/error';

import { SEO_TITLE_SUFFIX } from '@root/constants';
import Line from 'components/Line';

type ErrorPageProps = {
  statusCode?: number;
  hasGetInitialPropsRun?: boolean;
  err?: Error;
};

export default function ErrorPage({ statusCode, hasGetInitialPropsRun, err }: ErrorPageProps) {
  if (!hasGetInitialPropsRun && err) {
    // getInitialProps is not called in case of
    // https://github.com/vercel/next.js/issues/8592. As a workaround, we pass
    // err via _app.js so it can be captured
    Bugsnag.notify(err, (event) => {
      event.severity = 'error';
      event.unhandled = true;
    });
  }

  return (
    <div className="m-section-hero container flex flex-wrap items-center justify-around">
      <img
        id="page-not-found-img"
        alt="404"
        src="https://gentux.imgix.net/1550091090_404-error-image.png?auto=format"
        width="250px"
        height="485px"
      />
      <div className="mb-32">
        <h1 className="text-h1-display">{statusCode} Error</h1>

        <p>Oops! We're not quite ready.</p>

        <Line />

        <Link href="/" className="btn-info btn">
          Go Home
        </Link>
      </div>
    </div>
  );
}

ErrorPage.getInitialProps = async ({ req, res, err, asPath }: NextPageContext) => {
  const errorInitialProps = await NextErrorComponent.getInitialProps({
    res,
    err,
    pathname: asPath || '',
    query: {},
    AppTree: App,
  });

  // Workaround for https://github.com/vercel/next.js/issues/8592, mark when
  // getInitialProps has run
  // @ts-ignore
  errorInitialProps.hasGetInitialPropsRun = true;

  // Running on the server, the response object (`res`) is available.
  //
  // Next.js will pass an err on the server if a page's data fetching methods
  // threw or returned a Promise that rejected
  //
  // Running on the client (browser), Next.js will provide an err if:
  //
  //  - a page's `getInitialProps` threw or returned a Promise that rejected
  //  - an exception was thrown somewhere in the React lifecycle (render,
  //    componentDidMount, etc) that was caught by Next.js's React Error
  //    Boundary. Read more about what types of exceptions are caught by Error
  //    Boundaries: https://reactjs.org/docs/error-boundaries.html

  if (err) {
    Bugsnag.notify(err, (ev) => {
      ev.severity = 'error';
      ev.unhandled = true;
      // @ts-ignore
      ev.request = req;
    });

    const statusCode = res ? res.statusCode : err ? err.statusCode : null;

    return {
      statusCode,
      seo: {
        title: `${statusCode} Error${SEO_TITLE_SUFFIX}`,
        metaDescription: "Oops! We're not quite ready.",
      },
    };
  }

  // If this point is reached, getInitialProps was called without any
  // information about what the error might be. This is unexpected and may
  // indicate a bug introduced in Next.js, so record it in Bugsnag
  Bugsnag.notify(new Error(`_error.js getInitialProps missing data at path: ${asPath}`), (event) => {
    event.severity = 'error';
    event.unhandled = true;
    // @ts-ignore
    event.request = req;
  });

  return errorInitialProps;
};
